import quickfireFreeGamesComponent from "quickfire/components/quickfire-free-games-component";
import { convertQFGridToSlotworksGrid, convertQFSymbolPositions } from "quickfire/integration/util/quickfire-utils";
import quickfireModel from "quickfire/model/quickfire-model";
import quickfire from "quickfire/quickfire";
import Gameplay from "slotworks/model/gameplay/gameplay";
import FreespinRecord from "slotworks/model/gameplay/records/freespin-record";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import FreespinRequestPayload from "slotworks/model/gameplay/requests/freespin-request-payload";
import SpinRequestPayload from "slotworks/model/gameplay/requests/spin-request-payload";
import slotDefinition from "slotworks/model/slot-definition";
import builderManager from "slotworks/server/builders/builder-manager";
import { RecordBuilder } from "slotworks/server/builders/record-builder";
import { asArray, firstInArray, lastInArray } from "slotworks/utils/collection-utils";

export class SpinRecordBuilder implements RecordBuilder {
    /**
     * When a game has some interaction which changes the reels, quickfire send the reels before and after this interaction
     * Depending on the game's needs you can either use the un-changed reels (default) or the final changed reels (set this to true)
     */
    public useLastReel: boolean = false;

    public filter(request: any, response: any): boolean {
        return request instanceof SpinRequestPayload || request instanceof FreespinRequestPayload;
    }

    public build(gameplay: Gameplay, request: any, packet: QuickfireResponsePacket) {

        const player = packet.Response.Player;
        const slot = packet.Response.Slot;

        const qfStage = slot.attributes.stage.toLowerCase();

        const qfResult = lastInArray(asArray(slot.ResultSets.Result).filter((value) => value.attributes.name.toLowerCase() === qfStage));
        const qfReel = this.useLastReel ? lastInArray(qfResult.Reels) : firstInArray(qfResult.Reels);

        const isFreespin = qfStage === "freespins";
        const record = this.resolveRecordType(gameplay, request, packet);

        const grid = convertQFGridToSlotworksGrid(qfReel.attributes.symbols);
        record.grid = grid;

        record.reelset = slotDefinition.reelsets.get("base");

        record.cashWon = parseInt(slot.attributes.win);

        record.results = [];
        if (slot.Wins && slot.Wins.Win) {

            const wins = asArray(slot.Wins.Win);

            for (const win of wins) {
                record.results.push(...builderManager.buildResult(record, win, packet));
            }
        }

        if (packet.Response.Tokens && packet.Response.Tokens.Token) {

            const tokens = asArray(packet.Response.Tokens.Token);

            for (const token of tokens) {
                record.results.push(...builderManager.buildResult(record, token, packet));
            }
        }

        this.addStickyWilds(record, packet);

        record.hasChildren = !isFreespin && parseInt(slot.attributes.freeSpinTotalAwarded) > 0;
        record.lastChild = parseInt(slot.attributes.freeSpins) === 0;

        gameplay.balance = Math.floor(player.attributes.balance);

        if ((!isFreespin || record.lastChild) && packet.Response.FreeGames && packet.Response.FreeGames.CurrentGame) {
            this.updateFreeGames(packet.Response.FreeGames.CurrentGame);
        }

        quickfireModel.transactionNumber = packet.Response.Player.attributes.transNumber;

        return [record];
    }

    protected resolveRecordType(gameplay: Gameplay, request: any, packet: QuickfireResponsePacket): SpinRecord {
        const stage: string = packet.Response.Slot.attributes.stage.toLowerCase();
        const inFreespins: boolean = stage === "freespins";
        return inFreespins ? new FreespinRecord() : new SpinRecord();
    }

    private addStickyWilds(record: SpinRecord, packet: QuickfireResponsePacket) {
        const stickies: string[][] = record.overlayGrid = [];
        for (const reel of slotDefinition.matrixGrid) {
            stickies.push([]);
            for (let row = 0; row < reel; row++) {
                stickies[stickies.length - 1].push(null);
            }
        }

        if (packet.Response.Slot.StickyWilds && packet.Response.Slot.StickyWilds.StickyWild) {
            for (const qfSticky of asArray(packet.Response.Slot.StickyWilds.StickyWild)) {
                const position = convertQFSymbolPositions(qfSticky.attributes.index)[0];

                stickies[position.x][position.y] = quickfire.symbolNameMap.get(qfSticky.attributes.symbol);
            }
        }
    }

    private updateFreeGames(currentGame: QuickfireCurrentFreeGame) {
        if (quickfireModel.freeGames && quickfireModel.freeGames.length) {
            const freeGame = quickfireModel.freeGames[0];
            if (!quickfireModel.isFreeGameExpired(freeGame)) {
                freeGame.gamesPlayed = parseInt(currentGame.attributes.gamesPlayed);
                freeGame.gamesRemaining = parseInt(currentGame.attributes.gamesRemaining);
                freeGame.sumWinnings = parseInt(currentGame.attributes.sumWinnings);

                quickfireFreeGamesComponent.update();
            }
        }
    }
}

export default new SpinRecordBuilder();
